// src/pages/VoucherApproval.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Button, Modal, Row, Col, Form, Badge } from 'react-bootstrap';
import { Tooltip } from '@mui/material';
import { MdFilterList, MdDoneAll, MdClose, MdRemoveRedEye, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import loading from '../../../assets/images/common/loading.gif';
import DataTable from 'react-data-table-component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const VoucherApproval = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData || '{}');

  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [rowsSrc, setRowsSrc] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('APPROVE');
  const [actionRemarks, setActionRemarks] = useState('');
  const [actionTargetIds, setActionTargetIds] = useState([]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [voucherDetails, setVoucherDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  // Filter form state
  const [form, setForm] = useState({
    action: 'FILTER',
    status: 'PENDING',
    voucher_no: '',
    vendor_name: '',
    from_date: '',
    to_date: '',
    min_amount: '',
    max_amount: '',
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return ;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format amount with commas
  const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Fetch voucher details
  const fetchVoucherDetails = async (voucher_entry_id) => {
    if (voucherDetails[voucher_entry_id]) return; // Already loaded
    
    setLoadingDetails(prev => ({ ...prev, [voucher_entry_id]: true }));
    
    try {
      const response = await axios.get(
        `${baseUrl}/voucher/details/${voucher_entry_id}`
      );
      
      setVoucherDetails(prev => ({
        ...prev,
        [voucher_entry_id]: response.data || []
      }));
    } catch (error) {
      console.error("Error fetching voucher details:", error);
      toast.error("Failed to load voucher details");
      setVoucherDetails(prev => ({
        ...prev,
        [voucher_entry_id]: []
      }));
    } finally {
      setLoadingDetails(prev => ({ ...prev, [voucher_entry_id]: false }));
    }
  };

  // Expanded component for subgrid
  const ExpandedComponent = ({ data }) => {
    const details = voucherDetails[data.voucher_entry_id] || [];
    const isLoading = loadingDetails[data.voucher_entry_id];
    
    return (
      <div 
        className="expandedTableWrapper"
        style={{
          marginLeft: "60px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "16px",
          background: "#f9f9f9",
          marginTop: "10px",
          marginBottom: "10px"
        }}
      >
        <div style={{
          marginBottom: "12px",
          fontSize: "16px",
          fontWeight: "600",
          color: "#495057",
          borderBottom: "1px solid #dee2e6",
          paddingBottom: "8px"
        }}>
          Voucher Details for {data.voucher_no}
        </div>
        
        {isLoading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading voucher details...</span>
          </div>
        ) : details.length > 0 ? (
          <div style={{ overflow: "auto" }}>
            <table className="table table-bordered table-hover" style={{ 
              fontSize: "14px",
              margin: 0,
              minWidth: "100%"
            }}>
              <thead style={{ backgroundColor: "#e9ecef" }}>
                <tr>
                  <th style={{ padding: "10px", fontWeight: "600", whiteSpace: "nowrap" }}>A/C Code</th>
                  <th style={{ padding: "10px", fontWeight: "600", whiteSpace: "nowrap" }}>Sub Code</th>
                  <th style={{ padding: "10px", fontWeight: "600", whiteSpace: "nowrap" }}>Party State</th>
                  <th style={{ padding: "10px", fontWeight: "600", whiteSpace: "nowrap" }}>Amount</th>
                  <th style={{ padding: "10px", fontWeight: "600", whiteSpace: "nowrap" }}>Scope</th>
                  <th style={{ padding: "10px", fontWeight: "600", whiteSpace: "nowrap" }}>Tax Details</th>
                </tr>
              </thead>
              <tbody>
                {details.map((item, index) => (
                  <tr key={item.voucher_details_id || index}>
                    <td style={{ padding: "10px" }}>{item.account_code || '—'}</td>
                    <td style={{ padding: "10px" }}>{item.sub_account_code || '—'}</td>
                    <td style={{ padding: "10px" }}>{item.party_state_id || '—'}</td>
                    <td style={{ padding: "10px", textAlign: "right" }}>
                      {formatAmount(item.amount)}
                    </td>
                    <td style={{ padding: "10px" }}>{item.debit_credit || '—'}</td>
                    <td style={{ padding: "10px" }}>
                      <MdRemoveRedEye 
                        style={{ cursor: "pointer", color: "#4477b9" }}
                        title="View Tax Details"
                        onClick={() => {
                          // Handle tax details view
                          toast.info("Tax details view would open here");
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {/* {details.length > 0 && (
                  <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                    <td colSpan="3" style={{ padding: "10px", textAlign: "right" }}>Total:</td>
                    <td style={{ padding: "10px", textAlign: "right" }}>
                      {formatAmount(details.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                )} */}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-3" style={{ color: "#6c757d" }}>
            No voucher details found
          </div>
        )}
      </div>
    );
  };

  // Fetch pending vouchers
  const fetchPendingVouchers = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${baseUrl}/voucherentry/pendingapproval/`,
        { params: { school_id: userObj.school_id } }
      );
      
      const data = response.data?.pending_voucher_entries || [];
      setRowsSrc(data);
      
      // Clear old details when fetching new data
      setVoucherDetails({});
      setLoadingDetails({});
      
    } catch (error) {
      console.error("Error fetching pending vouchers:", error);
      toast.error("Failed to load vouchers");
      setRowsSrc([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle expand row
  const handleExpandClick = async (voucher_entry_id) => {
    await fetchVoucherDetails(voucher_entry_id);
  };

  // Columns configuration
  const columns = [
    {
  name: '',
  width: '50px',
  cell: (row) => {
    if (row.voucher_entry_id === "No Records Found") return null;

    return (
      <input
        type="checkbox"
        checked={selectedIds.has(row.voucher_entry_id)}
        onChange={(e) => {
          const next = new Set(selectedIds);
          if (e.target.checked) next.add(row.voucher_entry_id);
          else next.delete(row.voucher_entry_id);
          setSelectedIds(next);
        }}
      />
    );
  },
  sortable: false,
},

    {
      name: 'School Name',
      selector: row => row.school_name || '—',
      sortable: true,
      cell: (row) => (
        <Tooltip title={row.school_name}>
          <span className="cell-wrap">{row.school_name }</span>
        </Tooltip>
      ),
      minWidth: '150px',
    },
    {
      name: 'Voucher Type',
      selector: row => row.voucher_type_name ,
      sortable: true,
      cell: (row) => (
        <Tooltip title={row.voucher_type_name}>
          <span className="cell-wrap">{row.voucher_type_name}</span>
        </Tooltip>
      ),
      minWidth: '120px',
    },
   {
  name: 'Voucher No',
  selector: row => row.voucher_no ,
  sortable: true,
  cell: (row) => {
    // ✅ No Records row message HERE
    if (row.voucher_entry_id === "No Records Found") {
      return (
        <span style={{ color: "red", fontWeight: 600 }}>
          No Records Found
        </span>
      );
    }

    return (
      <Tooltip title={row.voucher_no}>
        <span className="cell-wrap">{row.voucher_no || '—'}</span>
      </Tooltip>
    );
  },
  minWidth: '140px',
},

    {
      name: 'Voucher Date',
      selector: row => row.voucher_date,
      sortable: true,
      cell: (row) => {
        const formattedDate = formatDate(row.voucher_date);
        return (
          <Tooltip title={formattedDate}>
            <span className="cell-wrap">{formattedDate}</span>
          </Tooltip>
        );
      },
      minWidth: '120px',
    },
    // {
    //   name: 'Amount',
    //   selector: row => row.amount || 0,
    //   sortable: true,
    //   cell: (row) => {
    //     const formattedAmount = formatAmount(row.amount);
    //     return (
    //       <Tooltip title={`₹${formattedAmount}`}>
    //         <span className="cell-wrap" style={{ textAlign: 'right', display: 'block' }}>
    //           ₹{formattedAmount}
    //         </span>
    //       </Tooltip>
    //     );
    //   },
    //   minWidth: '120px',
    //   right: true,
    // },
    {
  name: 'Status',
  selector: row => row.status || 'PENDING',
  sortable: true,
  cell: (row) => {
    // ❌ Do NOT show status for No Records row
    if (row.voucher_entry_id === "No Records Found") {
      return null;
    }

    return (
      <Badge bg="warning" style={{ fontSize: '0.8em' }}>
        PENDING
      </Badge>
    );
  },
  minWidth: '100px',
},

  ];

  // Bulk actions
  const bulkApprove = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      toast.info('Select at least one voucher.');
      return;
    }
    approveVouchersDirect(ids);
  };

  const approveVouchersDirect = async (ids) => {
    try {
      setIsLoading(true);
      const payload = {
        voucher_entry_ids: ids.join(','),
        created_by: userObj?.username || userObj?.user_name || 'system',
      };

      await axios.post(
        `${baseUrl}/voucherentry/approve`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      toast.success(`Approved ${ids.length} voucher${ids.length > 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      await fetchPendingVouchers();

    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
        err?.message ||
        'Approval failed'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmitFilter = async (e) => {
    e?.preventDefault?.();
    // Implement filter logic if needed
    setShowFilterModal(false);
    toast.info("Filter functionality would be implemented here");
  };

  const handleResetFilter = () => {
    setForm({
      action: 'FILTER',
      status: 'PENDING',
      voucher_no: '',
      vendor_name: '',
      from_date: '',
      to_date: '',
      min_amount: '',
      max_amount: '',
    });
    fetchPendingVouchers();
  };

  // Initial data fetch
  useEffect(() => {
    fetchPendingVouchers();
  }, []);

  // Search filter
  const searchableColumns = [
    (row) => row.school_name,
    (row) => row.voucher_type_name,
    (row) => row.voucher_no,
    (row) => row.voucher_date ? formatDate(row.voucher_date) : '',
    (row) => row.amount ? `₹${formatAmount(row.amount)}` : ''
  ];

  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) return rowsSrc;
    
    const query = searchQuery.toUpperCase().replace(/[-\s]+/g, '');
    return rowsSrc.filter((item) =>
      searchableColumns.some((selector) => {
        const value = selector(item);
        const stringValue = String(value || '').toUpperCase().replace(/[-\s]+/g, '');
        return stringValue.includes(query);
      })
    );
  }, [rowsSrc, searchQuery]);
  const noRecordsRow = {
  voucher_entry_id: "No Records Found",
  // school_name: "",
  // voucher_type_name: "",
  // voucher_no: "",
  // voucher_date: "",
  // status: ""
};


  return (
    <div className="pageBody">
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <h6 className="commonTableTitle">Voucher Approval</h6>
            <span className="badge bg-secondary">{filteredRecords.length} vouchers</span>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search ..."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
              // style={{ minWidth: '250px' }}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            {/* <OverlayTrigger placement="top" overlay={<Tooltip id="t1">Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={() => setShowFilterModal(true)}>
                <MdFilterList />
              </Button>
            </OverlayTrigger> */}

           <OverlayTrigger placement="top" overlay={<Tooltip id="t2"></Tooltip>}>
  <Button 
    className="primaryBtn" 
    variant="primary" 
    onClick={bulkApprove}
    disabled={selectedIds.size === 0}
  >
    Approve <MdDoneAll />
  </Button>
</OverlayTrigger>

          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          // ) : filteredRecords.length === 0 ? (
          //   <div className="text-center p-5">
          //     <p>No pending vouchers found.</p>
          //     <Button variant="primary" onClick={fetchPendingVouchers}>
          //       Refresh
          //     </Button>
          //   </div>
          ) : (
            <DataTable
  className="custom-table"
  columns={columns}
  data={
    Array.isArray(filteredRecords) && filteredRecords.length > 0
      ? filteredRecords
      : [noRecordsRow]
  }
  expandableRows={
    Array.isArray(filteredRecords) && filteredRecords.length > 0
  }
  expandableRowsComponent={ExpandedComponent}
  expandableRowExpanded={(row) =>
    row.voucher_entry_id &&
    row.voucher_entry_id !== "No Records Found" &&
    voucherDetails[row.voucher_entry_id]
  }
  onRowExpandToggled={(expanded, row) => {
    if (
      expanded &&
      row.voucher_entry_id &&
      row.voucher_entry_id !== "No Records Found"
    ) {
      handleExpandClick(row.voucher_entry_id);
    }
  }}
  expandableRowsHideExpander={false}
  pagination={
    Array.isArray(filteredRecords) && filteredRecords.length > 0
  }
  paginationPerPage={10}
  paginationRowsPerPageOptions={[10, 20, 50]}
  highlightOnHover
  responsive
  fixedHeader
  fixedHeaderScrollHeight="calc(100vh - 250px)"
  conditionalRowStyles={[
    {
      when: (row) => row.voucher_entry_id === "No Records Found",
      style: {
        textAlign: "center",
        fontSize: "16px",
        color: "red",
        backgroundColor: "#f9f9f9",
      },
    },
  ]}
  customStyles={{
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
        fontWeight: "600",
      },
    },
    rows: {
      style: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      },
    },
    expanderButton: {
      style: {
        cursor: "pointer",
        fontSize: "20px",
        fontWeight: "bold",
        color: "#007bff",
      },
    },
  }}
/>

          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter Vouchers</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Row>
            <Col xs={12} md={6}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select id="status" value={form.status} onChange={handleInputChange}>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="">All</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Voucher No</Form.Label>
                  <Form.Control
                    id="voucher_no"
                    value={form.voucher_no}
                    placeholder="Enter voucher no"
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12} md={6}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>From Date</Form.Label>
                  <Form.Control type="date" id="from_date" value={form.from_date} onChange={handleInputChange} />
                </Form.Group>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>To Date</Form.Label>
                  <Form.Control type="date" id="to_date" value={form.to_date} onChange={handleInputChange} />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12} md={6}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Min Amount</Form.Label>
                  <Form.Control
                    type="number"
                    id="min_amount"
                    value={form.min_amount}
                    placeholder="0"
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Max Amount</Form.Label>
                  <Form.Control
                    type="number"
                    id="max_amount"
                    value={form.max_amount}
                    placeholder="100000"
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleResetFilter}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={() => setShowFilterModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleSubmitFilter}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VoucherApproval;